"use client";

import { useState, useCallback, useEffect } from "react";
import { Toaster, toast } from "sonner";
import NautilusHeader from "./components/NautilusHeader";
import BirthDataForm, { type BirthFormData } from "./components/BirthDataForm";
import GeneratingAnimation from "./components/GeneratingAnimation";
import ReportDisplay from "./components/ReportDisplay";
import { type ReportData } from "./components/reportData";
import { createClient } from "@/lib/supabase/client";

type PageState = "form" | "generating" | "report";

const SYSTEM_PROMPT = `You are QuantuMe Nautilus, a deeply insightful archetypal intelligence system. Your role is to generate a personalized Avatar Blueprint report based on a person's birth data using Astrosophia (or Embodied Astrology) translating cosmic patterns into lived consciousness to generate an Avatar Blueprint in motion.

You MUST respond with ONLY a valid JSON object — no markdown, no explanation, no code fences. The JSON must exactly match this TypeScript interface:

{
  "snapshot": {
    "primaryArchetype": string,
    "coreTheme": string,
    "keyStrength": string,
    "growthEdge": string,
    "lifeDirection": string
  },
  "essence": {
    "archetypeOverview": string,
    "coreTraits": string[],
    "strengths": string[],
    "growthChallenges": string[]
  },
  "blueprint": {
    "personalityAnalysis": string,
    "lifePathInsights": string,
    "cosmicInfluences": string,
    "relatedArchetypes": [
      { "name": string, "description": string }
    ]
  },
  "alignmentPlan": {
    "weeks": [
      {
        "theme": string,
        "actions": string[]
      }
    ]
  },
  "avatarStatement": string,
  "finalMessage": string
}

Use the person's name, birth date, birth time (if provided), and birthplace to craft a deeply personalized, meaningful report. Draw on numerology, astrology, archetypal psychology, and life path wisdom. Make every section feel uniquely written for this specific person — not generic. Use their first name naturally throughout the text.`;

export default function ReportGeneratorPage() {
  const [pageState, setPageState] = useState<PageState>("form");
  const [formData, setFormData] = useState<BirthFormData | null>(null);
  const [reportData, setReportData] = useState<ReportData | null>(null);
  const [payerEmail, setPayerEmail] = useState<string | null>(null);
  const [isPaidFromEmail, setIsPaidFromEmail] = useState(false);

  // On mount: check if URL contains ?email= from receipt link and verify payment
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const emailParam = params.get("email");
    if (!emailParam) return;

    const normalizedEmail = decodeURIComponent(emailParam).trim().toLowerCase();

    const verifyPayment = async () => {
      try {
        const supabase = createClient();
        const { data, error } = await supabase
          .from("nautilus_payments")
          .select("id, payment_status, report_generated, customer_email")
          .ilike("customer_email", normalizedEmail)
          .eq("payment_status", "succeeded")
          .eq("report_generated", false)
          .order("created_at", { ascending: false })
          .limit(1);

        if (!error && data && data.length > 0) {
          setPayerEmail(data[0].customer_email ?? normalizedEmail);
          setIsPaidFromEmail(true);
        } else {
          console.warn("Payment verification: no matching succeeded payment found for", normalizedEmail, error);
        }
      } catch (err) {
        console.error("Payment verification from email link failed:", err);
      }
    };

    verifyPayment();
  }, []);

  const handleFormSubmit = useCallback(async (data: BirthFormData) => {
    setFormData(data);
    setPageState("generating");

    const firstName = data.name.trim().split(" ")[0];
    const userMessage = `Please generate an Avatar Blueprint report for:
- Full Name: ${data.name}
- Date of Birth: ${data.birthdate}
- Time of Birth: ${data.birthtime || "Unknown"}
- Place of Birth: ${data.birthplace}

Create a deeply personalized report for ${firstName}. Return ONLY the JSON object.`;

    try {
      const res = await fetch("/api/ai/chat-completion", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          provider: "OPEN_AI",
          model: "gpt-4o",
          messages: [
          { role: "system", content: SYSTEM_PROMPT },
          { role: "user", content: userMessage }],

          stream: false,
          parameters: {
            max_completion_tokens: 4000
          }
        })
      });

      const contentType = res.headers.get("content-type") || "";
      if (!contentType.includes("application/json")) {
        const text = await res.text();
        console.error("Non-JSON response from API:", text.slice(0, 500));
        throw new Error(`Server error (${res.status}): AI service returned an unexpected response. Please try again.`);
      }

      const result = await res.json();

      if (!res.ok || result.error) {
        throw new Error(result.error || result.details || `Request failed: ${res.status}`);
      }

      // Extract content from the OpenAI response structure
      const content =
      result?.choices?.[0]?.message?.content ||
      result?.content?.[0]?.text ||
      result?.content ||
      result?.text || (
      typeof result === "string" ? result : null);

      if (!content) {
        console.error("Empty content from AI response. Full result:", JSON.stringify(result));
        throw new Error("Empty response received from AI provider");
      }

      // Strip markdown fences and extract JSON
      let cleaned = content.trim();
      cleaned = cleaned.replace(/^```(?:json)?\s*/i, "").replace(/\s*```$/i, "").trim();
      const jsonMatch = cleaned.match(/\{[\s\S]*\}/);
      if (!jsonMatch) {
        throw new Error("No JSON object found in response");
      }

      const parsed: ReportData = JSON.parse(jsonMatch[0]);
      setReportData(parsed);
      setPageState("report");
    } catch (err) {
      console.error("Report generation error:", err);
      const message = err instanceof Error ? err.message : "Unknown error";
      toast.error(
        message.includes("API key") ?
        "AI service configuration error. Please contact support." :
        `Failed to generate your report: ${message}. Please try again.`
      );
      setPageState("form");
    }
  }, []);

  // Mark report as generated in DB after successful generation
  const handleReportGenerated = useCallback(async () => {
    if (!payerEmail) return;
    try {
      const supabase = createClient();
      await supabase.
      from("nautilus_payments").
      update({ report_generated: true }).
      eq("customer_email", payerEmail).
      eq("payment_status", "succeeded").
      eq("report_generated", false);
    } catch (err) {
      console.error("Failed to mark report as generated:", err);
    }
  }, [payerEmail]);

  const handleReset = useCallback(() => {
    setPageState("form");
    setFormData(null);
    setReportData(null);
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, []);

  return (
    <>
      {/* Background cosmic decoration */}
      <div
        className="fixed inset-0 pointer-events-none overflow-hidden"
        aria-hidden="true"
        style={{ zIndex: 0 }}>

        {/* Top-left nebula */}
        <div
          className="absolute"
          style={{
            top: "-20%",
            left: "-10%",
            width: "60%",
            height: "60%",
            background: "radial-gradient(ellipse, rgba(124,62,97,0.08) 0%, transparent 70%)"
          }} />

        {/* Bottom-right nebula */}
        <div
          className="absolute"
          style={{
            bottom: "-20%",
            right: "-10%",
            width: "50%",
            height: "50%",
            background: "radial-gradient(ellipse, rgba(0,18,88,0.12) 0%, transparent 70%)"
          }} />

        {/* Center glow */}
        <div
          className="absolute"
          style={{
            top: "30%",
            left: "50%",
            transform: "translateX(-50%)",
            width: "40%",
            height: "40%",
            background: "radial-gradient(ellipse, rgba(219,184,77,0.03) 0%, transparent 70%)"
          }} />

        {/* Stars */}
        {[
        { top: "15%", left: "8%", size: 1.5, opacity: 0.4 },
        { top: "25%", left: "92%", size: 2, opacity: 0.3 },
        { top: "45%", left: "5%", size: 1, opacity: 0.5 },
        { top: "65%", left: "95%", size: 1.5, opacity: 0.35 },
        { top: "80%", left: "12%", size: 2, opacity: 0.25 },
        { top: "10%", left: "55%", size: 1, opacity: 0.4 },
        { top: "90%", left: "70%", size: 1.5, opacity: 0.3 },
        { top: "35%", left: "88%", size: 1, opacity: 0.45 },
        { top: "70%", left: "3%", size: 1.5, opacity: 0.3 },
        { top: "50%", left: "98%", size: 1, opacity: 0.4 }].
        map((star, i) =>
        <div
          key={i}
          className="absolute rounded-full"
          style={{
            top: star.top,
            left: star.left,
            width: `${star.size * 2}px`,
            height: `${star.size * 2}px`,
            background: "#dbb84d",
            opacity: star.opacity,
            animation: `pulse ${2 + i % 3}s ease-in-out infinite`,
            animationDelay: `${i * 0.3}s`
          }} />

        )}
      </div>

      {/* App Shell */}
      <div className="relative min-h-screen flex flex-col" style={{ zIndex: 1 }}>
        <NautilusHeader />

        <main className="flex-1 w-full max-w-screen-2xl mx-auto px-4 sm:px-6 lg:px-10 xl:px-16 py-10 md:py-16 10, 45, 0.92)] border-none bg-[rgba(54,13,54,0)]">
          {/* FORM STATE */}
          {pageState === "form" &&
          <div className="fade-in">
              <BirthDataForm
              onSubmit={handleFormSubmit}
              isLoading={false}
              payerEmail={payerEmail}
              onReportGenerated={handleReportGenerated}
              isPaidFromEmail={isPaidFromEmail} />

            </div>
          }

          {/* GENERATING STATE */}
          {pageState === "generating" &&
          <div className="flex items-center justify-center min-h-[60vh] fade-in">
              <GeneratingAnimation name={formData?.name ?? ""} />
            </div>
          }

          {/* REPORT STATE */}
          {pageState === "report" && reportData && formData &&
          <div className="fade-in">
              <ReportDisplay
              report={reportData}
              formData={formData}
              onReset={handleReset} />

            </div>
          }
        </main>

        {/* Footer */}
        <footer
          className="w-full py-6 text-center no-print border-none"
          style={{ borderTop: "0px solid rgba(219,184,77,0.08)", backgroundColor: "#0a0614" }}>

          <div className="flex items-center justify-center gap-2">
            <img
              src="/assets/images/logo-QuantuMe-wh-100-1774294006575.png"
              alt="QuantuMe Logo"
              className="h-6 w-auto object-contain opacity-50" />

            <p className="text-xs text-white/40" style={{ fontWeight: 400 }}>
              © 2026{" "}
              <a
                href="https://quantume.earth"
                target="_blank"
                rel="noopener noreferrer"
                className="text-white/40 hover:text-white/60 underline">

                QuantuMe LLC
              </a>{" "}
              · All insights are for personal reflection and growth purposes. Not intended as psychological, medical, or financial advice.
            </p>
          </div>
        </footer>
      </div>

      {/* Sonner Toast Provider */}
      <Toaster
        position="bottom-right"
        toastOptions={{
          style: {
            background: "rgba(54,23,64,0.95)",
            border: "1px solid rgba(219,184,77,0.3)",
            color: "#f0e8f5",
            fontFamily: "'DM Sans', sans-serif",
            fontSize: "0.875rem",
            backdropFilter: "blur(12px)"
          }
        }} />

    </>);

}